import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { concatLatestFrom } from '@ngrx/operators';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  concatMap,
  EMPTY,
  filter,
  forkJoin,
  map,
  Observable,
  of,
  pairwise,
  scan,
  take,
} from 'rxjs';

import { TableInfo } from '@blaze-shared/api-contracts-domain';
import {
  ConnectorConfiguration,
  ConnectorSetupPhase,
  Typeform,
} from '@blaze-shared/connectors-domain';
import {
  CopyConnectorConfigEnvButtonComponent,
  DialogModule,
  EnvironmentIndicatorComponent,
  SelectComponent,
} from '@blaze-shared/shared-feature';
import {
  ButtonBarComponent,
  ButtonComponent,
  ElementLoadingIndicatorComponent,
  PanelComponent,
  PanelRef,
  TooltipDirective,
} from '@blaze-shared/shared-ui';
import { SelectOption } from '@blaze-shared/shared-util';
import { IconsModule } from '@blaze-shared/theming-data-access';

import { BaseConnectorDialogComponent } from '../../__base-connector-dialog/base-connector-dialog.component';
import { MappingRowComponent } from '../../mapping-row/mapping-row.component';
import { MappingOption } from '../../models/data-connector-dialog';
import { SecretInputComponent } from '../../secret-input/secret-input.component';
import { prepareConnectorConfiguration, updateFormArrayControlsLengthMutably } from '../../utils';
import { WebhookIdInputComponent } from '../../webhook-id-input/webhook-id-input.component';

import {
  ConnectorForm,
  FormMapping,
  LoadingState,
  TypeformConfiguration,
  TypeformMapping,
} from './typeform-connector-dialog.models';

@UntilDestroy()
@Component({
  selector: 'blaze-typeform-connector-dialog',
  templateUrl: './typeform-connector-dialog.component.html',
  styleUrls: ['./typeform-connector-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    ButtonBarComponent,
    ButtonComponent,
    CopyConnectorConfigEnvButtonComponent,
    DialogModule,
    ElementLoadingIndicatorComponent,
    EnvironmentIndicatorComponent,
    IconsModule,
    MappingRowComponent,
    MatTooltipModule,
    NgTemplateOutlet,
    PanelComponent,
    ReactiveFormsModule,
    SecretInputComponent,
    SelectComponent,
    TooltipDirective,
    WebhookIdInputComponent,
  ],
  host: { class: 'data-connector-dialog' },
})
export class TypeformConnectorDialogComponent
  extends BaseConnectorDialogComponent<TypeformConfiguration>
  implements OnInit
{
  private readonly panelRef = inject(PanelRef);

  private availableForms$$ = new BehaviorSubject<Typeform.FormInfo[]>([]);
  private availableMappings$$ = new BehaviorSubject<MappingOption[]>([]);
  private formSchemas$$ = new BehaviorSubject<Typeform.FormSchema[]>([]);
  private loading$$ = new BehaviorSubject<LoadingState | undefined>(undefined);

  protected readonly webhookBaseUrl = '/typeform/webhook';

  serviceName = 'typeform';
  connectorForm: FormGroup<ConnectorForm>;

  availableFormsOptions$: Observable<SelectOption<string>[]>;
  availableMappings$ = this.availableMappings$$.asObservable();
  loading$: Observable<LoadingState | undefined>;

  get canRemoveMapping(): boolean {
    return this.connectorForm.controls.mappings.length > 1;
  }

  get isTableSelectDisabled(): boolean {
    return !this.connectorForm.value.projectId;
  }

  constructor() {
    super();

    this.connectorForm = new FormGroup<ConnectorForm>({
      connectorName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      webhookId: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      secret: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      formId: new FormControl(null, { validators: [Validators.required] }),
      projectId: new FormControl(null, { validators: [Validators.required] }),
      tableId: new FormControl(null, { validators: [Validators.required] }),
      mappings: new FormArray<FormGroup<FormMapping>>([this.createMappingControls()]),
    });

    this.withOAuth();

    const formId$ = this.connectorForm.controls.formId.valueChanges;
    const projectId$ = this.connectorForm.controls.projectId.valueChanges;
    const tableId$ = this.connectorForm.controls.tableId.valueChanges;

    this.setupPhase$
      .pipe(
        pairwise(),
        filter(
          ([prevPhase, currPhase]) =>
            prevPhase === ConnectorSetupPhase.Install &&
            currPhase === this.SetupPhase.ConnectorCreation,
        ),
        concatMap(() =>
          forkJoin({
            forms: this.loadForms(this.authState),
            projects: this.loadProjects(),
          }),
        ),
      )
      .subscribe({
        next: ({ forms, projects }) => {
          this.availableForms$$.next(forms.formInfos);
          this.availableProjects$$.next(projects);
          this.loading$$.next({ forms: false, projects: false });
        },
        error: (error) => this.handleErrors(error),
      });

    projectId$
      .pipe(
        concatMap((projectId) => {
          this.connectorForm.patchValue({ tableId: null });
          this.loading$$.next({ tables: true });
          if (!projectId) {
            return EMPTY;
          }

          return this.loadTables(projectId);
        }),
        untilDestroyed(this),
      )
      .subscribe((tables) => {
        this.availableTables$$.next(tables.filter((table) => !table.systemTableType));
        this.loading$$.next({ tables: false });
      });

    combineLatest({
      tableId: tableId$,
      formId: formId$,
    })
      .pipe(
        concatMap(({ tableId, formId }) => {
          if (!tableId || !formId || !this.authState) {
            return EMPTY;
          }
          return this.loadFormSchemas(this.authState, formId, tableId);
        }),
        untilDestroyed(this),
      )
      .subscribe((schemas) => {
        this.formSchemas$$.next(schemas);
      });

    combineLatest({
      formSchemas: this.formSchemas$$,
      tableId: tableId$,
    })
      .pipe(
        untilDestroyed(this),
        concatLatestFrom(() => this.availableTables$$),
        map(([{ formSchemas, tableId }, tables]) => {
          const table = tables.find((table) => table.id === tableId);
          return this.determineAvailableMappings(table, formSchemas);
        }),
      )
      .subscribe((availableMappings) => {
        this.availableMappings$$.next(availableMappings);
      });

    this.loading$ = this.loading$$.pipe(
      scan(
        (acc, curr) => {
          return {
            ...acc,
            forms: curr?.forms || false,
            projects: curr?.projects || false,
            tables: curr?.tables || false,
          };
        },
        { forms: false, projects: false, tables: false, connector: false } as LoadingState,
      ),
    );

    this.availableFormsOptions$ = this.availableForms$$.pipe(
      map((forms) =>
        forms.map<SelectOption<string>>((form) => ({ label: form.title, value: form.id })),
      ),
    );
  }

  ngOnInit(): void {
    if (this.connector) {
      this.fillForm(this.getConfiguration(this.connector));
    } else {
      this.goToAuth();
    }
  }

  getConfiguration(connector?: ConnectorConfiguration): TypeformConfiguration {
    const connectorId = connector?.id ?? '';
    const connectorName = connector?.name ?? '';
    const configuration = connector?.configuration ?? {};

    const projectId = String(configuration?.projectId ?? '');
    const tableId = projectId ? String(configuration?.tableId ?? '') : '';
    const formId = String(configuration?.formId ?? '');
    const authState = String(configuration?.authState ?? '');
    const secret = String(configuration?.secret ?? '');
    const webhookId = String(configuration?.id ?? '');

    let mappings: TypeformMapping[] = [];
    if (tableId) {
      mappings = configuration?.mappings
        ? (configuration?.mappings as unknown as TypeformMapping[])
        : [];
    }

    return {
      authState,
      connectorId,
      connectorName,
      formId,
      mappings,
      projectId,
      secret,
      tableId,
      webhookId,
    };
  }

  fillForm(configuration: TypeformConfiguration): void {
    const { authState, projectId, formId, tableId, connectorId, connectorName, webhookId, secret } =
      configuration;

    this.authState = authState;
    this.connectorId = connectorId;
    this.setupPhase$$.next(ConnectorSetupPhase.ConnectorCreation);
    this.loadingDialog$$.next(true);

    combineLatest({
      projects: this.loadProjects(),
      tables: this.loadTables(projectId),
      forms: this.loadForms(authState),
      formSchemas: formId ? this.loadFormSchemas(authState, formId, tableId) : of([]),
    })
      .pipe(untilDestroyed(this), take(1))
      .subscribe(({ projects, tables, forms, formSchemas }) => {
        this.availableProjects$$.next(projects);
        this.availableTables$$.next(tables);
        this.availableForms$$.next(forms.formInfos);
        this.formSchemas$$.next(formSchemas);

        const selectedTable = tables.find((table) => table.id === tableId);
        this.availableMappings$$.next(this.determineAvailableMappings(selectedTable, formSchemas));

        if (formId) {
          const mappings = configuration.mappings.map(({ id, columnId }) => ({
            fieldId: id,
            columnId,
          }));

          updateFormArrayControlsLengthMutably(
            this.connectorForm.controls.mappings,
            mappings.length,
            () => this.createMappingControls(),
          );

          this.connectorForm.patchValue(
            {
              connectorName,
              webhookId,
              secret,
              formId,
              projectId,
              tableId,
              mappings,
            },
            { emitEvent: false },
          );
        } else {
          this.connectorForm.patchValue(
            {
              connectorName,
              webhookId,
              secret,
            },
            { emitEvent: false },
          );
        }

        this.loadingDialog$$.next(false);
      });
  }

  loadForms(authState: string): Observable<Typeform.FormsInfoResponse> {
    if (!authState) {
      return of({ formInfos: [], page: 0 });
    }

    return this.connectorsApiService.getTypeformForms(authState).pipe(
      catchError((error) => {
        this.handleErrors(error);
        return of({ formInfos: [], page: 0 });
      }),
    );
  }

  loadFormSchemas(
    authState: string,
    formId: string,
    tableId: string,
  ): Observable<Typeform.FormSchema[]> {
    return this.connectorsApiService.postTypeformSchemas(authState, formId, tableId).pipe(
      catchError((error) => {
        this.handleErrors(error);
        return of([]);
      }),
    );
  }

  determineAvailableMappings(
    table: TableInfo | undefined,
    formSchemas: Typeform.FormSchema[] | undefined = [],
  ): MappingOption[] {
    if (!table) {
      return [];
    }

    return formSchemas.map((schema) => {
      const columns = schema.allowedColumnIds.map((columnId) => {
        const column = table.columns.find((col) => col.id === columnId);

        return {
          value: columnId,
          label: column?.name ?? 'No name found!',
        };
      });

      return {
        value: schema.id,
        label: schema.title,
        columns,
      };
    });
  }

  createMappingControls(
    fieldId: string | null = null,
    columnId: string | null = null,
  ): FormGroup<FormMapping> {
    return new FormGroup<FormMapping>({
      fieldId: new FormControl(fieldId, { validators: [Validators.required] }),
      columnId: new FormControl(columnId, { validators: [Validators.required] }),
    });
  }

  addMapping(): void {
    this.connectorForm.controls.mappings.push(this.createMappingControls());
  }

  removeMapping(index: number): void {
    this.connectorForm.controls.mappings.removeAt(index);
  }

  generateSecret(): void {
    this.connectorForm.controls.secret.setValue(crypto.randomUUID());
  }

  saveConnector(): void {
    if (this.connectorForm.invalid) {
      return;
    }

    const { connectorName, webhookId, formId, secret, projectId, tableId, mappings } =
      this.connectorForm.value;

    if (!connectorName || !webhookId || !formId || !projectId || !tableId) {
      return;
    }

    const mappingsParsed = (mappings ?? []).map((mapping) => ({
      id: mapping.fieldId,
      columnId: mapping.columnId,
    }));

    const connectorConfig = prepareConnectorConfiguration(
      connectorName,
      this.template?.id,
      this.accountId,
      this.connector,
      {
        authState: this.authState,
        id: webhookId,
        secret: secret ?? '',
        projectId,
        tableId,
        formId,
        mappings: mappingsParsed as any,
      },
    );

    this.loading$$.next({ connector: true });

    this.saveOrUpdateConnector(connectorConfig, this.connector?.id)
      .pipe(untilDestroyed(this))
      .subscribe((connector) => {
        if (connector) {
          this.panelRef.close();
        }
        this.loading$$.next({ connector: false });
      });
  }
}
